import React from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { reduceCreativesData, reduceNameBasedData, shouldShowTotalActivities } from 'helpers/data';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { validateStatisticsData } from 'helpers/validate';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchTacticPlacements } from 'services/tactics/duck';
import { fetchChannel, fetchChannelCallouts, fetchChannelCreatives, fetchChannelDynamicCreatives, fetchChannelTargetingTactics, requestChannelUpsell, showedChannelUpsell } from 'services/channel/duck';
import { fetchActivities } from 'services/activities/duck';
import { setObjectiveShadow } from 'services/config/duck';


// Components and Containers
import LayerThreeDashboardComponent from 'containers/dashboards/DashboardBaseComponent';
import { Grid, Row } from 'react-bootstrap';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import ExportPDF from 'containers/buttons/ExportPDF';
import ChannelUpsell from 'components/molecules/ChannelUpsell';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';
import TileWithValidation from 'components/molecules/TileWithValidation';
import ObjectiveSwitcher from 'components/molecules/ObjectiveSwitcher';

// Charts
import StackedAreaChart from 'components/molecules/charts/StackedAreaChart';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import GroupedBarChart from 'components/molecules/charts/GroupedBarChart';
import ActivitiesStackedAreaChart from 'components/molecules/charts/ActivitiesStackedAreaChart';

// Tables
import ActivitiesTable from 'components/molecules/tables/ActivitiesTable';
import PlacementsTable from 'components/molecules/tables/PlacementsTable';
import ProtectedCreativesReports from 'components/organisms/ProtectedCreativesReports';
import ProtectedDynamicCreativesReports from 'components/organisms/ProtectedDynamicCreativesReports';
import ProtectedTargetingTacticsTable from 'components/organisms/ProtectedTargetingTacticsTable';

class Dashboard extends LayerThreeDashboardComponent {
    constructor(props) {
        super(props, 'display');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();

        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchChannel(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchChannelCreatives(payload);
        this.props.fetchChannelDynamicCreatives(payload);
        this.props.fetchTacticPlacements(payload);
        this.props.fetchChannelTargetingTactics(payload);
        this.props.fetchActivities(payload);
    }

    render() {
        const stub = `l3-${this.getCurrentChannel()}`;
        const {
            backfillBuckets,
            xAxisLabel,
            entityObjective,
            showTotalActivities,
            showTargetingTactics,
            showConversionRevenue,
            selectedTimeRange,
            entityObjectiveLabelCode,
            hideBudgetTimeCharts,
        } = this.props;
        const {
            isLabelCoded,
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm,
            showActivitiesData,
            calloutTitle,
            primaryActivityField,
            primaryActivityFieldTerm,
            singularTerm,
            helpKeySuffix,
            ecpaTerm,
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const hasCalloutData = get(this.props, 'channel.callouts.params.hasData', false);
        const hasPlacementsData = get(this.props, 'placements.params.hasData', null);
        const hasTargetingTacticsData = get(this.props, 'channel.targetingTactics.params.hasData', null);
        const hasChannelData = get(this.props, 'channel.hasData', false);
        const hasCreativesData = get(this.props, 'channel.creatives.params.hasData', null);
        const hasDynamicCreativesData = get(this.props, 'channel.dynamicCreatives.params.hasData', null);

        const callouts = get(this.props, 'channel.callouts.data', {});
        const data = get(this.props, 'channel.data', {});
        const placements = get(this.props, 'placements', {});
        const targetingTactics = get(this.props, 'channel.targetingTactics', {});
        const creatives = get(this.props, 'channel.creatives', {});
        const calloutWindow = get(this.props, 'channel.callouts.params.timeRanges.previous.windowSize', null);

        const activities = get(this.props, 'activities', {});
        const hasActivities = get(this.props, 'activities.hasData', null);
        const activitiesRenderFlag = hasActivities && (showActivitiesData || showTotalActivities);

        let placementsData = null;
        const placementsParams = get(this.props, 'placements.params', {});
        if (hasPlacementsData) {
            placementsData = reduceNameBasedData({
                data: placements,
                namesArrayKey: 'placements',
                injectedKey: 'placementName'
            });
        }

        let targetingTacticsData = {};
        const targetingTacticsParams = get(this.props, 'channel.targetingTactics.params', {});
        if (hasTargetingTacticsData) {
            targetingTacticsData = reduceNameBasedData({
                data: targetingTactics,
                namesArrayKey: 'targetingTactics',
                injectedKey: 'targetingTacticName'
            });
        }

        let creativesData = null;
        const creativesParams = get(this.props, 'channel.creatives.params', {});

        if (hasCreativesData) {
            creativesData = reduceCreativesData(creatives);
        }

        const isChannelAvailable = get(this.props, 'entities.meta.channelAvailability.display', false);

        const calloutSettings = [
            {
                key: 'impressions',
                title: 'Impressions',
                format: '0,0'
            },
            {
                key: eCPAActivitiesOrClicks,
                helpKey: `${eCPAActivitiesOrClicks}${helpKeySuffix}`,
                title: calloutTitle,
                format: '0,0'
            },
        ];

        if (showActivitiesData) {
            calloutSettings.push({
                key: 'ecpa',
                helpKey: `ecpa${helpKeySuffix}`,
                title: entityObjectiveLabelCode ? `Average ${ecpaTerm}` : 'eCPA',
                format: '$0,0.00',
                invert: true
            });
        } else {
            calloutSettings.push({ key: 'ctr', title: 'CTR', format: '0.00%' });
        }

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        const upsellSettings = this.props.entityMeta.upsellSettings;
        const upsellRequested = get(this.props, 'entityMeta.upsellRequested', {});

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} />}
        </React.Fragment>;

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Display
                </ChannelTitle>
                {this.props.enableObjectiveSwitching && <ObjectiveSwitcher
                    setObjectiveShadow={this.props.setObjectiveShadow}
                    fetchEntityMeta={this.props.fetchEntityMeta}
                    entityId={this.getCurrentEntityId()}
                    objective={this.props.entityObjective}
                    objectiveLabelCode={this.props.entityObjectiveLabelCode}
                />}
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs */}
                <Row>
                    {calloutComponents}
                </Row>
                {/* Charts */}
                {hasChannelData ? (
                    <TwoAxisBarWithLineChart
                        title={`IMPRESSIONS VS. ${eCPAActivitiesOrClicksTerm.toUpperCase()}`}
                        exportName={`${stub}-impressions-vs-kpi`}
                        data={data}
                        yBar="impressions"
                        yLine={eCPAActivitiesOrClicks}
                        yLineTitle={eCPAActivitiesOrClicksTerm}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        tactic={this.state.channel}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData} />
                )}

                {hasPlacementsData ? (
                    <GroupedBarChart
                        title="PLACEMENT PERFORMANCE"
                        exportName={`${stub}-placement-performance`}
                        data={placementsData.graph}
                        keys={placementsData.sortedKeys}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        y={eCPAActivitiesOrClicks}
                        yAxisLabel={eCPAActivitiesOrClicksTerm}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasPlacementsData} />
                )}

                {hasPlacementsData ? (
                    <PlacementsTable
                        title={`PLACEMENT ${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-placement-activity-breakdown`}
                        data={placementsData.table}
                        params={placementsParams}
                        hasData={hasPlacementsData}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['placement', 'impressions', 'clicks', 'ctr', 'spend'] :
                                undefined
                        }
                    />
                ) : (
                    <RenderingTile height='400px' show={hasPlacementsData} />
                )}

                <ProtectedTargetingTacticsTable
                    title="TARGETING TACTICS BREAKDOWN"
                    exportName={`${stub}-targeting-tactics-breakdown`}
                    tableType="targetingTactics"
                    data={targetingTacticsData.table}
                    params={targetingTacticsParams}
                    hasData={hasTargetingTacticsData}
                    showTotalActivities={showTotalActivities}
                    showTargetingTactics={showTargetingTactics}
                    showConversionRevenue={showConversionRevenue}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    accessors={
                        entityObjective === 'ctr' ?
                            ['targetingTactic', 'impressions', 'clicks', 'ctr'] :
                            undefined
                    } />

                {activitiesRenderFlag ? (
                    <ActivitiesStackedAreaChart
                        title={`${singularTerm.toUpperCase()} TRENDS`}
                        exportName={`${stub}-activity-trends`}
                        exportLimit={10}
                        data={activities.data}
                        keys={activities.rankings[primaryActivityField].map(d => d.name).reverse()}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel={primaryActivityFieldTerm}
                        y={primaryActivityField}
                        colors={this.props.theme.orderedColors}
                        animateDuration={2000}
                        categories={activities.categories} />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {activitiesRenderFlag ? (
                    <ActivitiesTable
                        title={`${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-website-activity-breakdown`}
                        data={activities}
                        showTotalActivities={showTotalActivities}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        objectives={[entityObjective]}
                        filterField={showTotalActivities ? 'total_activities' : primaryActivityField}
                    />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                <ProtectedCreativesReports
                    exportName={`${stub}-creatives`}
                    hasCreativesData={hasCreativesData}
                    creativesData={creativesData}
                    creativesParams={creativesParams}
                    eCPAActivitiesOrClicks={eCPAActivitiesOrClicks}
                    eCPAActivitiesOrClicksTerm={eCPAActivitiesOrClicksTerm}
                    showTotalActivities={showTotalActivities}
                    showConversionRevenue={showConversionRevenue}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    accessors={
                        entityObjective === 'ctr' ?
                            ['label', 'size', 'impressions', 'clicks', 'ctr'] :
                            undefined
                    }
                />

                <ProtectedDynamicCreativesReports
                    exportName={`${stub}-dynamic-creatives`}
                    hasDynamicCreativesData={hasDynamicCreativesData}
                    dynamicCreatives={get(this.props, 'channel.dynamicCreatives', {})}
                    dynamicProfiles={get(this.props, 'entityMeta.dynamicProfiles', {})}
                    entityObjective={entityObjective}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                />

                <TileWithValidation
                    hasData={hasChannelData && !hideBudgetTimeCharts}
                    innerComponent={StackedAreaChart}
                    innerProps={{
                        title: 'BUDGET REPORT',
                        exportName: `${stub}-budget-report`,
                        data: { [this.state.channel]: data },
                        y: 'spend',
                        xAxisLabel,
                        backfillBuckets,
                        yAxisLabel: 'Budget',
                        yOptions: { formatByType: 'priceWithoutSpace' },
                        toolTipOptions: { pointFormatter: toolTipPointFormatterGenerator },
                        yAxisOptions: {
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        },
                        animateDuration: 2000,
                        xAxisType: 'datetime',
                        validation: {
                            key: 'spend',
                            data: this.props.channel.statistics,
                            validator: validateStatisticsData
                        }
                    }}
                />

            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    Display
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {upsellSettings.enabled && upsellSettings.display ? (
                    <ChannelUpsell
                        channel='display'
                        channelDisplay='Display'
                        icon='icon-display-fill'
                        successfulRequest={upsellRequested.display}
                        buttonClick={() => {
                            const entityId = this.getCurrentEntityId();
                            const channel = this.getCurrentChannel();
                            return this.props.requestChannelUpsell({ entityId, channel });
                        }}
                        onMount={() => {
                            const entityId = this.getCurrentEntityId();
                            const channel = this.getCurrentChannel();
                            return this.props.showedChannelUpsell({ entityId, channel });
                        }}
                    />
                ) : (
                    <NoChannelDataTile channel='Display' />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityMeta: state.entities.meta,
    objectiveShadow: state.config.objectiveShadow,
    enableObjectiveSwitching: get(state, 'entities.meta.meta.enableObjectiveSwitching', false),
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    showTargetingTactics: get(state, 'entities.meta.meta.showTargetingTactics', false),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    hideBudgetTimeCharts: get(state, 'entities.meta.meta.hideBudgetTimeCharts', false),
    channel: state.channel.display,
    placements: state.tactics.placements,
    activities: state.activities.display,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', [])
});

export default connect(mapStateToProps, {
    fetchActivities,
    setObjectiveShadow,
    fetchEntityMeta,
    fetchChannel,
    fetchChannelCallouts,
    fetchChannelCreatives,
    fetchChannelDynamicCreatives,
    fetchTacticPlacements,
    fetchChannelTargetingTactics,
    requestChannelUpsell,
    showedChannelUpsell,
})(Dashboard);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerThreeDisplayContainer.js